import {
  getFormBuilderComponents,
  removeFormBuilderEmptySpaces,
  setFormBuilderSelectedElement,
  removeFormBuilderMovingComponent,
  getFormBuilderSelectedElement,
  getElementUniqueId,
  getElementComponentData
} from "../../helpers/formBuilder";
import formServer from "../../server/form";
import formVersionServer from "../../server/formVersion";
import catalogServer from "../../server/catalog";

const initialFormJson = () => ({
  elType: "Form",
  id: "form1",
  ...getElementComponentData("Form").defaultAttrs,
  grid: {
    components: []
  },
  layout: {
    rows: []
  }
});

const newFormName = "New Form";
const newVersionId = -1;
const newVersionName = "New Version";
const initialVersion = {
  id: newVersionId,
  name: newVersionName,
  json: initialFormJson(),
  hasResults: false,
  createdAt: "",
  createdBy: {}
};
const initialForm = {
  id: null,
  name: newFormName,
  menu: null,
  documentInstructions: "",
  category: "",
  json: initialFormJson(),
  jsonChanged: true,
  metadataChanged: true,
  selectedElementControlId: null,
  versions: [{ ...initialVersion }],
  selectedVersionId: newVersionId,
  officialVersionId: newVersionId,
  movingComponentId: null,
  editingElement: false,
  draggingComponent: false
};

const formBuilderModule = {
  namespaced: true,
  state: () => ({
    list: {
      main: [],
      groupSession: [],
      masterTreatmentPlan: [],
      masterTreatmentPlanReview: [],
      doctorOrder: []
    },
    form: {},
    menus: []
  }),
  mutations: {
    initializeFormBuilderFormJson(state, { form, versions, category }) {
      let newForm = initialForm;
      if (form.isNew) {
        newForm.id = null;
        newForm.json = initialFormJson();
        newForm.jsonChanged = true;
        newForm.name = newFormName;
        newForm.menu = null;
        newForm.documentInstructions = "";
        newForm.showInOmt = false;
        newForm.metadataChanged = true;
        newForm.versions = [{ ...initialVersion }];
        newForm.selectedVersionId = newVersionId;
        newForm.officialVersionId = newVersionId;
      } else {
        newForm.id = form.id;
        newForm.name = form.name;
        newForm.menu = form.menuId;
        newForm.documentInstructions = form.documentInstructions;
        newForm.showInOmt = form.showInOmt;
        newForm.json = versions.find(
          version => version.id == form.current.id
        ).content;
        newForm.jsonChanged = false;
        newForm.metadataChanged = false;
        newForm.selectedVersionId = form.current.id;
        newForm.officialVersionId = form.current.id;
        newForm.versions = versions.map(version => ({
          id: version.id,
          name: version.name,
          hasResults: version.hasResults,
          json: version.content,
          createdAt: version.createdAt,
          createdBy: version.createdBy
        }));
      }
      newForm.category = category;
      newForm.selectedElementControlId = null;
      newForm.movingComponentId = null;
      newForm.editingElement = false;
      newForm.draggingComponent = false;
      state.form[form.id] = { ...newForm };
    },
    setFormBuilderFormJson(state, { newJson, formId }) {
      const cleanedJson = removeFormBuilderEmptySpaces(newJson);
      const newForms = { ...state.form };
      newForms[formId] = {
        ...newForms[formId],
        json: cleanedJson,
        jsonChanged: true
      };
      state.form = newForms;
    },
    setFormBuilderFormData(state, { data, formId }) {
      const newForms = { ...state.form };
      newForms[formId] = {
        ...newForms[formId],
        ...data
      };
      state.form = newForms;
    },
    setFormBuilderSelectedElementControlId(
      state,
      { selectedElementControlId, formId }
    ) {
      const newForms = { ...state.form };
      newForms[formId].selectedElementControlId = selectedElementControlId;
      state.form = newForms;
    },
    setFormBuilderSelectedElement(state, { newSelectedElement, formId }) {
      let newJson = setFormBuilderSelectedElement(
        { ...state.form[formId].json },
        newSelectedElement
      );
      const newForms = { ...state.form };
      newForms[formId].json = JSON.parse(JSON.stringify(newJson));
      newForms[formId].jsonChanged = true;
      state.form = newForms;
    },
    addFormBuilderVersion(state, { formId }) {
      const newForms = { ...state.form };
      newForms[formId].versions.push({
        id: newVersionId,
        name: newVersionName,
        json: initialFormJson()
      });
      newForms[formId].selectedVersionId = newVersionId;
      newForms[formId].json = initialFormJson();
      newForms[formId].jsonChanged = true;
      state.form = newForms;
    },
    setFormBuilderVersionSelected(state, { versionId, formId }) {
      const newForms = { ...state.form };
      newForms[formId].selectedVersionId = versionId;
      newForms[formId].json = state.form[formId].versions.find(
        version => version.id == versionId
      ).json;
      state.form = newForms;
    },
    setFormBuilderVersionOfficial(state, { versionId, formId }) {
      const newForms = { ...state.form };
      newForms[formId].officialVersionId = versionId;
      state.form = newForms;
    },
    setFormBuilderSelectedVersionName(state, { newName, formId }) {
      const newForms = { ...state.form };
      newForms[formId].versions.find(
        version => version.id == state.form[formId].selectedVersionId
      ).name = newName;
      newForms[formId].metadataChanged = true;
      state.form = newForms;
    },
    setFormBuilderMovingComponent(state, { movingComponentId, formId }) {
      const newForms = { ...state.form };
      if (!newForms[formId].movingComponentId || !movingComponentId)
        newForms[formId].movingComponentId = movingComponentId;
      state.form = newForms;
    },
    deleteFormBuilderMovingComponent(state, { formId }) {
      let newJson = removeFormBuilderMovingComponent(
        { ...state.form[formId].json },
        state.form[formId].movingComponentId
      );
      const newForms = { ...state.form };
      newForms[formId].json = JSON.parse(JSON.stringify(newJson));
      newForms[formId].movingComponentId = null;
      state.form = newForms;
    },
    setFormBuilderEditingElement(state, { editing, formId }) {
      const newForms = { ...state.form };
      newForms[formId].editingElement = editing;
      state.form = newForms;
    },
    setFormBuilderDragging(state, { dragging, formId }) {
      const newForms = { ...state.form };
      newForms[formId].draggingComponent = dragging;
      state.form = newForms;
    }
  },
  actions: {
    addFormBuilderForm(context, { formId }) {
      return formServer.create({
        name: context.state.form[formId].name,
        content: context.state.form[formId].json,
        menuId: context.state.form[formId].menu,
        category: context.state.form[formId].category,
        current: null,
        documentInstructions: context.state.form[formId].documentInstructions,
        showInOmt: context.state.form[formId].showInOmt
      });
    },
    addFormBuilderFormToContext(context, { formId, newForm }) {
      const newForms = { ...context.state.form };
      newForms[formId].id = newForm.id;
      context.state.form = newForms;
      return newForm.id;
    },
    async editFormBuilderForm(context, { formId }) {
      return formServer.update(context.state.form[formId].id, {
        name: context.state.form[formId].name,
        content: context.state.form[formId].json,
        menuId: context.state.form[formId].menu,
        category: context.state.form[formId].category,
        current: context.state.form[formId].officialVersionId,
        documentInstructions: context.state.form[formId].documentInstructions,
        showInOmt: context.state.form[formId].showInOmt
      });
    },
    async deleteFormBuilderForm(context, { id, category }) {
      await formServer.delete(id);
      context.dispatch("fetchFormBuilderForms", { category });
    },
    async addFormBuilderFormVersion(context, { formId }) {
      const version = context.state.form[formId].versions.find(
        version => version.id == context.state.form[formId].selectedVersionId
      );
      const newVersion = await formVersionServer.create({
        name: "version-name",
        content: context.state.form[formId].json,
        formId: context.state.form[formId].id
      });
      version.id = newVersion.id;
      version.json = context.state.form[formId].json;
      const newForms = { ...context.state.form };
      if (context.state.form[formId].selectedVersionId == -1)
        newForms[formId].selectedVersionId = newVersion.id;
      if (context.state.form[formId].officialVersionId == -1)
        newForms[formId].officialVersionId = newVersion.id;
      context.state.form = newForms;
    },
    async editFormBuilderFormVersion(context, { formId }) {
      const version = context.state.form[formId].versions.find(
        version => version.id == context.state.form[formId].selectedVersionId
      );
      const newForms = { ...context.state.form };
      if (version.hasResults && context.state.form[formId].jsonChanged) {
        const newVersion = await formVersionServer.create({
          name: "version-name",
          content: context.state.form[formId].json,
          formId: context.state.form[formId].id
        });

        newForms[formId].selectedVersionId = newVersion.id;
        newForms[formId].officialVersionId = newVersion.id;
        newForms[formId].versions.push({
          id: newVersion.id,
          name: version.name,
          json: { ...context.state.form[formId].json }
        });
      } else if (context.state.form[formId].jsonChanged) {
        await formVersionServer.update(version.id, {
          name: version.name,
          content: context.state.form[formId].json,
          formId: context.state.form[formId].id
        });
      }

      newForms[formId].jsonChanged = false;
      newForms[formId].metadataChanged = false;
      context.state.form = newForms;
    },
    async deleteFormBuilderFormVersion(context, id) {
      await formVersionServer.delete(id);
    },
    async fetchFormBuilderForms(context, { category }) {
      const forms = await formServer.query({ category });
      context.state.list[category] = forms;
    },
    async fetchFormBuilderMenus(context) {
      const menus = await catalogServer.getMenus();
      context.state.menus = menus;
    }
  },
  getters: {
    formBuilderSelectedElement: state => ({ formId }) => {
      return getFormBuilderSelectedElement(
        state.form[formId].json,
        state.form[formId].selectedElementControlId
      );
    },
    getFormBuilderFormById: () => async id => {
      return await formServer.get(id);
    },
    getFormBuilderFormVersionsByFormId: () => async id => {
      return await formVersionServer.query(id);
    },
    getFormBuilderFormVersionsById: () => async id => {
      return await formServer.getVersions(id);
    },
    getFormBuilderComponents: state => ({ formId }) => {
      return getFormBuilderComponents(state.form[formId].json);
    },
    getFormBuilderFormVersionById: () => async id => {
      return await formVersionServer.get(id);
    },
    getElementUniqueId: state => ({ elementType, formId }) => {
      return getElementUniqueId(state.form[formId].json.layout, elementType);
    }
  }
};

export default formBuilderModule;
